import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiUser, FiUpload } from 'react-icons/fi';
import { useAuth } from '../../../context/AuthContext';
import { storageService } from '../../../services/storage.service';

const ProfileStep = ({ formData, onInputChange, onNext, onBack, isValid }) => {
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [errors, setErrors] = useState({});
  const { user } = useAuth();

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingAvatar(true);
      setErrors({});
      
      // Validate file type and size
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        throw new Error('Image size should be less than 5MB');
      }

      // Upload avatar and get URL
      const { url, path } = await storageService.updateAvatar(
        user.uid, 
        file,
        formData.profile.avatar?.path
      );
      
      // Update form data with file, URL and path
      onInputChange('profile', 'avatar', {
        file,
        url,
        path
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setErrors(prev => ({ ...prev, avatar: error.message }));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setErrors(prev => ({ ...prev, name: '' }));
    
    if (value.length > 50) {
      setErrors(prev => ({ ...prev, name: 'Name is too long' }));
      return;
    }
    
    onInputChange('profile', 'displayName', value);
  };

  const handleBirthdayChange = (e) => {
    const value = e.target.value;
    setErrors(prev => ({ ...prev, birthday: '' }));
    
    const birthDate = new Date(value);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    
    if (age < 13) {
      setErrors(prev => ({ ...prev, birthday: 'You must be at least 13 years old' }));
      return;
    }
    
    onInputChange('profile', 'birthday', value);
  };

  const calculateMaxDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 13);
    return date.toISOString().split('T')[0];
  };

  // Check if the form is valid
  const isFormValid = () => {
    return (
      formData.profile.displayName.trim() !== '' &&
      formData.profile.birthday &&
      !errors.name &&
      !errors.birthday
    );
  };

  return (
    <motion.div 
      className="onboarding-step profile-step"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
    >
      <h2>Your Profile</h2>
      <p className="step-description">
        Let's personalize your experience
      </p>

      <div className="profile-form">
        <motion.div 
          className="avatar-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <p className="avatar-optional">Profile picture (optional)</p>
          <div className="avatar-container">
            {formData.profile.avatar ? (
              <img 
                src={formData.profile.avatar.url || URL.createObjectURL(formData.profile.avatar.file)} 
                alt="Profile preview"
                className="avatar-preview" 
              />
            ) : (
              <div className="avatar-placeholder">
                <FiUser />
              </div>
            )}
            <label className={`avatar-upload-btn ${uploadingAvatar ? 'uploading' : ''}`}>
              {uploadingAvatar ? (
                <div className="upload-spinner" />
              ) : (
                <FiUpload />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                disabled={uploadingAvatar}
                hidden
              />
            </label>
          </div>
          {errors.avatar && <div className="input-error">{errors.avatar}</div>}
        </motion.div>

        <motion.div 
          className="profile-inputs"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="input-group">
            <input
              type="text"
              value={formData.profile.displayName}
              onChange={handleNameChange}
              placeholder="What should we call you?"
              className={`profile-input ${errors.name ? 'error' : ''}`}
              required
            />
            {errors.name && <div className="input-error">{errors.name}</div>}
          </div>

          <div className="input-group">
            <input
              type="date"
              value={formData.profile.birthday || ''}
              onChange={handleBirthdayChange}
              max={calculateMaxDate()}
              className={`profile-input ${errors.birthday ? 'error' : ''}`}
              placeholder="Your birthday"
              required
            />
            {errors.birthday && <div className="input-error">{errors.birthday}</div>}
          </div>
        </motion.div>
      </div>

      <div className="step-buttons">
        <button 
          className="onboarding-btn secondary"
          onClick={onBack}
        >
          Back
        </button>
        <button 
          className="onboarding-btn"
          onClick={onNext}
          disabled={!isFormValid()}
        >
          Continue
        </button>
      </div>
    </motion.div>
  );
};

export default ProfileStep; 